import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    List,
    ModelEvents,
} from '@denishussar/html5-game-engine';
import { AnimationContents } from '../../../AnimationContents';
import { AppStateEnum, GameModel } from '../../Models/GameModel';
import { GameLogic } from '../../Logic/GameLogic';
import { ButtonController, IButtonControllerOptions } from '../../Common/ButtonController';
import { SoundModel } from '../../Models/SoundModel';
import { Point, Text, TextMetrics } from 'pixi.js';
import {
    ILeaderboardScreenItemControllerOptions,
    LeaderboardScreenItemController,
} from './LeaderboardScreenItemController';
import { LocalizationContents } from '../../LocalizationContents';
import { Backend, IBackendLeaderboardDurationTypeEnum } from '../../Backend';
import {
    ILeaderboardScreenDurationItemControllerOptions,
    LeaderboardScreenDurationItemController,
} from './LeaderboardScreenDurationItemController';
import { resizeScreen } from '../../Utils/Resize';

export interface ILeaderboardScreenControllerOptions {
    fromGarage: boolean;
}

export class LeaderboardScreenController extends AnimationBaseController {
    $model: GameModel;

    private _width: number = 0;
    private _height: number = 0;

    private _duration: IBackendLeaderboardDurationTypeEnum = IBackendLeaderboardDurationTypeEnum.total;

    private _items: List<LeaderboardScreenItemController> = new List<LeaderboardScreenItemController>();

    private _itemStartPoint = new Point(0, -440);
    private _itemSizePoint = new Point(1077, 102);
    private _itemSpacingPoint = new Point(0, 10);

    public constructor(
        @Inject private _options: ILeaderboardScreenControllerOptions,
        @Inject private _gameLogic: GameLogic,
        @Inject private _modelEvents: ModelEvents,
        @Inject private soundLevels: SoundModel,
        @Inject private _backend: Backend,
    ) {
        super('LeaderboardScreenController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.LEADERBOARD_SCREEN_JSON,
            libraryName: 'leaderboard',
            childControllers: {
                btn_back: {
                    instanceName: 'btn_back',
                    controller: ButtonController,
                    model: this.$model,
                    options: <IButtonControllerOptions>{
                        onClick: () => {
                            if (this._options.fromGarage) {
                                this._gameLogic.setState(AppStateEnum.garage);
                            } else {
                                this._gameLogic.setState(AppStateEnum.endscreen);
                            }
                        },
                    },
                },
                button_current: {
                    instanceName: 'btn_current',
                    controller: LeaderboardScreenDurationItemController,
                    model: this.$model,
                    options: <ILeaderboardScreenDurationItemControllerOptions>{
                        onClick: () => this.updateItems(IBackendLeaderboardDurationTypeEnum.current),
                    },
                },
                button_total: {
                    instanceName: 'btn_total',
                    controller: LeaderboardScreenDurationItemController,
                    model: this.$model,
                    options: <ILeaderboardScreenDurationItemControllerOptions>{
                        onClick: () => this.updateItems(IBackendLeaderboardDurationTypeEnum.total),
                    },
                },
            },
        };
    }

    protected onInitialize(): void {
        this._width = this.display.width;
        this._height = this.display.height;
        this.updateItems(IBackendLeaderboardDurationTypeEnum.current);
    }

    protected updateItems(duration: IBackendLeaderboardDurationTypeEnum): void {
        this._duration = duration;

        const currentButton = this.$childControllers.get<LeaderboardScreenDurationItemController>('button_current');
        const totalButton = this.$childControllers.get<LeaderboardScreenDurationItemController>('button_total');

        switch (this._duration) {
            case IBackendLeaderboardDurationTypeEnum.current:
                currentButton.setActive(true);
                totalButton.setActive(false);
                break;

            case IBackendLeaderboardDurationTypeEnum.total:
                currentButton.setActive(false);
                totalButton.setActive(true);
                break;
        }

        this._items.forEach(item => this.removeChildController(item));
        this._items.clear();

        this.setTextByQuery('txt_title_type', this._duration === IBackendLeaderboardDurationTypeEnum.current ?
            LocalizationContents.getText('subtitle_leaderboard_3') :
            LocalizationContents.getText('subtitle_leaderboard_4'));

        this.localise();

        this._backend.getLeaderboard(this._duration === IBackendLeaderboardDurationTypeEnum.total)
            .then(({ top, currentPlayer }) => {
                let highlight = false;

                this._items.forEach(item => this.removeChildController(item));
                this._items.clear();

                let array = top
                    .concat([])
                    .sort((a, b) => {
                        if (parseInt(a.position) < parseInt(b.position)) {
                            return -1;
                        }
                        if (parseInt(a.position) > parseInt(b.position)) {
                            return 1;
                        }
                        return 0;
                    });

                if (currentPlayer) {
                    const arrayPlayer = array.find(o => o.user === currentPlayer.user);

                    if (arrayPlayer) {
                        const playerIndex = parseInt(arrayPlayer.position);

                        if (playerIndex > 10) {
                            array = array
                                .slice(0, 9)
                                .concat(currentPlayer)
                                .sort((a, b) => {
                                    if (parseInt(a.position) < parseInt(b.position)) {
                                        return -1;
                                    }
                                    if (parseInt(a.position) > parseInt(b.position)) {
                                        return 1;
                                    }
                                    return 0;
                                });
                        } else {
                            array = array.slice(0, 10);
                        }
                    }

                } else {
                    array = array.slice(0, 10);
                }

                array.forEach(user => {
                    this.addItem({
                        highlight: highlight,
                        isPlayer: currentPlayer ? user.user === currentPlayer.user : false,
                        index: parseInt(user.position),
                        name: user.nickname,
                        address: user.user,
                        value: parseInt(user.coins),
                    });

                    highlight = !highlight;
                });
            });

        // this.addItem({ index: 1, name: "name", car: "Delorian", type: LeaderboardScreenItemType.coins, value: 10, isPlayer: true })
    }

    protected onResize(width: number, height: number, scale: number): void {
        resizeScreen(width, height, scale, this);
    }

    protected onUpdate(frameRate: number): void {
    }

    private addItem(options: ILeaderboardScreenItemControllerOptions): void {
        const controller = this.addChildController({
            controller: LeaderboardScreenItemController,
            model: this.$model,
            options: options,
        }, `item_${this._items.count()}`) as LeaderboardScreenItemController;

        controller.y = this._itemStartPoint.y + this._itemSpacingPoint.y + ((this._itemSizePoint.y + this._itemSpacingPoint.y) * this._items.count());

        this._items.add(controller);
    }

    private localise() {
        this.setTextByQuery('txt_title', LocalizationContents.getText('title_leaderboard'));
        this.setTextByQuery('btn_current.label1', LocalizationContents.getText('button_current_tour'));
        this.setTextByQuery('btn_current.label2', LocalizationContents.getText('button_current_tour'));
        this.setTextByQuery('btn_total.label1', LocalizationContents.getText('button_alltime'));
        this.setTextByQuery('btn_total.label2', LocalizationContents.getText('button_alltime'));

        const current1 = this.getDisplayObjectByQuery('btn_current.label1')[0] as Text;
        const current2 = this.getDisplayObjectByQuery('btn_current.label2')[0] as Text;
        const total1 = this.getDisplayObjectByQuery('btn_total.label1')[0] as Text;
        const total2 = this.getDisplayObjectByQuery('btn_total.label2')[0] as Text;

        current1.y = -TextMetrics.measureText(current1.text, current1.style).height / 2;
        current2.y = -TextMetrics.measureText(current2.text, current2.style).height / 2;
        total1.y = -TextMetrics.measureText(total1.text, total1.style).height / 2;
        total2.y = -TextMetrics.measureText(total2.text, total2.style).height / 2;
    }
}
